import React from 'react';
import {Helmet} from 'react-helmet-async';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const {FiUsers, FiTarget, FiHeart, FiAward, FiMail} = FiIcons;

const ChiSiamo = () => {
  const values = [
    {
      icon: FiTarget,
      title: "Risultati Concreti",
      description: "Non promettiamo miracoli, ma lavoriamo per obiettivi misurabili e raggiungibili"
    },
    {
      icon: FiUsers,
      title: "Approccio Umano",
      description: "Ogni cliente è unico. Ascoltiamo, capiamo e creiamo soluzioni su misura"
    },
    {
      icon: FiHeart,
      title: "Passione Locale",
      description: "Siamo napoletani, conosciamo il territorio e aiutiamo le imprese locali a crescere"
    },
    {
      icon: FiAward,
      title: "Qualità Prima di Tutto",
      description: "Preferiamo fare meno progetti ma curarli nei minimi dettagli"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Chi Siamo - Realizzazione Siti Web Napoli | La Nostra Storia</title>
        <meta name="description" content="Scopri la storia di Realizzazione Siti Web Napoli: web agency specializzata in sviluppo web, SEO e marketing digitale per PMI napoletane." />
        <meta name="keywords" content="chi siamo web agency napoli,storia realizzazione siti napoli,web developer napoli" />
        <link rel="canonical" href="https://realizzazionesitiwebnapoli.com/chi-siamo" />
      </Helmet>

      {/* Hero Section - Velo scuro al 40% */}
      <section className="relative min-h-screen bg-napoli-gradient text-white overflow-hidden flex items-center justify-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=80&w=1920&auto=format&fit=crop')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="absolute inset-0 bg-black opacity-40"></div>
        <div className="relative container mx-auto px-4 py-20 text-center z-10">
          <div className="max-w-4xl mx-auto">
            <motion.h1 
              initial={{opacity: 0, y: 30}} 
              animate={{opacity: 1, y: 0}} 
              className="text-4xl lg:text-5xl font-poppins font-bold mb-6 text-white drop-shadow-lg mt-12 md:mt-0"
            >
              Chi siamo
            </motion.h1>
            <motion.p 
              initial={{opacity: 0, y: 30}} 
              animate={{opacity: 1, y: 0}} 
              transition={{delay: 0.2}}
              className="text-xl mb-8 text-white leading-relaxed drop-shadow-md"
            >
              Siamo una web agency napoletana specializzata in tecnologia e marketing digitale. La nostra missione è aiutare PMI e professionisti di Napoli a crescere online con soluzioni concrete e misurabili.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-napoli-light">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-poppins font-bold text-napoli-blu mb-6">I nostri valori</h2>
            <p className="text-xl text-napoli-blu font-medium">
              Quello che ci guida ogni giorno nel nostro lavoro
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div 
                key={index}
                initial={{opacity: 0, y: 30}} 
                whileInView={{opacity: 1, y: 0}} 
                transition={{duration: 0.6, delay: index * 0.1}}
                viewport={{once: true}}
                className="bg-white rounded-xl p-6 shadow-lg text-center"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-napoli-azzurro to-napoli-accent rounded-lg flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={value.icon} className="w-6 h-6 text-napoli-blu" />
                </div>
                <h3 className="text-lg font-poppins font-semibold text-napoli-blu mb-3">
                  {value.title}
                </h3>
                <p className="text-napoli-blu leading-relaxed font-medium">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-poppins font-bold text-napoli-blu mb-6">La nostra storia</h2>
            </div>
            <div className="bg-napoli-light rounded-xl p-8 shadow-lg">
              <p className="text-lg text-napoli-blu leading-relaxed mb-6 font-medium">
                Tutto è iniziato nel 2020, quando abbiamo deciso di specializzarci nell'aiutare le piccole imprese napoletane a digitalizzarsi. Quello che era nato come un progetto locale è diventato presto una realtà consolidata nel panorama del web design campano.
              </p>
              <p className="text-lg text-napoli-blu leading-relaxed mb-6 font-medium">
                Abbiamo iniziato realizzando siti web per ristoranti e negozi del centro storico. Vedere i nostri clienti aumentare le vendite e migliorare la visibilità ci ha spinti a crescere e specializzarci sempre di più in tutti gli aspetti del marketing digitale.
              </p>
              <p className="text-lg text-napoli-blu leading-relaxed font-medium">
                Oggi abbiamo aiutato oltre 120 aziende a crescere online con risultati misurabili. Continuiamo a essere orgogliosamente napoletani e a credere che ogni business, grande o piccolo, meriti di avere successo nel digitale.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="py-20 bg-napoli-light">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-poppins font-bold text-napoli-blu mb-6">La nostra esperienza</h2>
            <p className="text-xl text-napoli-blu font-medium">
              Competenze sviluppate in anni di lavoro sul campo
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div 
              initial={{opacity: 0, y: 30}} 
              whileInView={{opacity: 1, y: 0}} 
              transition={{duration: 0.6}}
              viewport={{once: true}}
              className="bg-white rounded-xl p-6 shadow-lg text-center"
            >
              <div className="text-4xl font-bold text-napoli-azzurro mb-3">5+</div>
              <h3 className="text-lg font-semibold text-napoli-blu mb-2">Anni di Esperienza</h3>
              <p className="text-napoli-blu font-medium">Nel settore del web design e marketing digitale</p>
            </motion.div>
            <motion.div 
              initial={{opacity: 0, y: 30}} 
              whileInView={{opacity: 1, y: 0}} 
              transition={{duration: 0.6, delay: 0.1}}
              viewport={{once: true}}
              className="bg-white rounded-xl p-6 shadow-lg text-center"
            >
              <div className="text-4xl font-bold text-napoli-azzurro mb-3">120+</div>
              <h3 className="text-lg font-semibold text-napoli-blu mb-2">Progetti Completati</h3>
              <p className="text-napoli-blu font-medium">Siti web, e-commerce e campagne marketing</p>
            </motion.div>
            <motion.div 
              initial={{opacity: 0, y: 30}} 
              whileInView={{opacity: 1, y: 0}} 
              transition={{duration: 0.6, delay: 0.2}}
              viewport={{once: true}}
              className="bg-white rounded-xl p-6 shadow-lg text-center"
            >
              <div className="text-4xl font-bold text-napoli-azzurro mb-3">4.9/5</div>
              <h3 className="text-lg font-semibold text-napoli-blu mb-2">Soddisfazione Clienti</h3>
              <p className="text-napoli-blu font-medium">Media delle recensioni ricevute</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section - Velo scuro al 40% */}
      <section className="relative py-20 overflow-hidden flex items-center justify-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1551434678-e076c223a692?q=80&w=1920&auto=format&fit=crop')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="absolute inset-0 bg-black opacity-40"></div>
        <div className="relative container mx-auto px-4 text-center z-10">
          <h2 className="text-3xl lg:text-4xl font-poppins font-bold mb-6 text-white drop-shadow-lg">Pronto per il tuo progetto digitale?</h2>
          <p className="text-xl mb-8 text-white max-w-2xl mx-auto font-medium drop-shadow-md">
            Contattaci per una consulenza gratuita e scopri come possiamo aiutare il tuo business a crescere online.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contatti" 
              className="bg-napoli-accent hover:bg-yellow-400 text-napoli-blu font-bold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-napoli-accent focus:ring-opacity-50 shadow-lg hover:shadow-xl text-lg"
            >
              Richiedi preventivo gratuito
            </Link>
            <a 
              href="mailto:info@realizzazionesitiwebnapoli.com" 
              className="bg-napoli-azzurro hover:bg-blue-500 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-napoli-azzurro focus:ring-opacity-50 shadow-lg hover:shadow-xl text-lg"
            >
              <SafeIcon icon={FiMail} className="w-5 h-5 mr-2 inline" />
              Scrivici una email
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default ChiSiamo;